import React, { useCallback, useEffect, useMemo } from "react";

import { buildUserpassCredentialFormatRules } from "v3/credentialFormatRules";
import { connect } from "react-redux";
import { compose } from "redux";
import { bool, func, objectOf, shape, string } from "prop-types";

import { RecoveryUserpassStep3 } from "@ui-kit/components/index.es";
import withExchangeToken from "pages/_components/withExchangeToken";
import { actions as layoutActions } from "reducers/layout";
import { actions as recoveryPasswordActions, selectors as recoveryPasswordSelectors } from "reducers/recoveryPassword";
import * as i18n from "util/i18n";
import Yup from "yup";
import * as configUtils from "util/config";

function RecoveryUserpassStep3V3({ dispatch, history, exchangeToken, resetCode, recoveryAsyncPending, fieldErrors }) {
    useEffect(() => {
        dispatch(layoutActions.setScopes(["recoveryUserpass"]));
        return () => {
            dispatch(layoutActions.setScopes([]));
        };
    }, [dispatch]);

    const minLength = configUtils.getInteger("core.password.minLength", 8);
    const maxLength = configUtils.getInteger("core.password.maxLength", 20);

    const initialValues = useMemo(
        () => ({
            password: "",
            passwordConfirmation: "",
        }),
        [],
    );

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                password: Yup.string()
                    .required(i18n.get("settings.changeUserpass.newUserpass.required"))
                    .min(minLength, i18n.get("settings.changePassword.modify.error.minLength"))
                    .max(maxLength, i18n.get("settings.changePassword.modify.error.maxLength"))
                    .matches(
                        new RegExp(configUtils.get("core.userconfiguration.updateuserpassword.pattern")),
                        i18n.get("settings.changePassword.modify.error.mustBeAlphanumeric"),
                    ),
                passwordConfirmation: Yup.string()
                    .required(i18n.get("settings.changeUserpass.newUserpassConfirmation.required"))
                    .oneOf([Yup.ref("password")], i18n.get("settings.changePassword.modify.error.usersMustMatch")),
            }),
        [minLength, maxLength],
    );

    const handleSubmit = useCallback(
        (values, formikBag) => {
            const newPassword = values.password ?? "";
            const newPasswordConfirmation = values.passwordConfirmation ?? "";

            const enhancedBag = {
                ...formikBag,
                props: { ...(formikBag.props || {}), location: { pathname: "/v3/recoveryUserpass/step3" }, history },
            };

            dispatch(
                recoveryPasswordActions.recoveryUserpassStep3(
                    newPassword,
                    newPasswordConfirmation,
                    exchangeToken,
                    resetCode,
                    enhancedBag,
                ),
            );
        },
        [dispatch, exchangeToken, resetCode],
    );

    const getLabels = useCallback((id) => {
        const keys = {
            newPassword: "recoveryUserpass.step3.password.label",
            newPasswordConfirmation: "recoveryUserpass.step3.passwordConfirmation.label",
            submit: "global.confirm",
        };
        return i18n.get(keys[id] ?? id);
    }, []);

    const getText = useCallback((id) => {
        const keys = {
            "recoveryUserpass.step3.message": "recoveryPassword.step3.credential.message",
        };
        return i18n.get(keys[id] ?? id);
    }, []);

    const credentialFormatRuleItems = useMemo(() => buildUserpassCredentialFormatRules(minLength, maxLength), [
        minLength,
        maxLength,
    ]);

    return (
        <RecoveryUserpassStep3
            initialValues={initialValues}
            validationSchema={validationSchema}
            handleSubmit={handleSubmit}
            getLabels={getLabels}
            getText={getText}
            submitLoading={recoveryAsyncPending}
            fieldErrors={fieldErrors ?? undefined}
            credentialFormatRuleItems={credentialFormatRuleItems}
        />
    );
}

RecoveryUserpassStep3V3.propTypes = {
    dispatch: func.isRequired,
    history: shape({ push: func }).isRequired,
    exchangeToken: string,
    resetCode: string,
    recoveryAsyncPending: bool,
    fieldErrors: objectOf(string),
};

RecoveryUserpassStep3V3.defaultProps = {
    exchangeToken: null,
    resetCode: null,
    recoveryAsyncPending: false,
    fieldErrors: undefined,
};

const mapStateToProps = (state) => ({
    exchangeToken: recoveryPasswordSelectors.getExchangeToken(state),
    resetCode: recoveryPasswordSelectors.getResetCode(state),
    recoveryAsyncPending: recoveryPasswordSelectors.getAsyncPending(state),
    fieldErrors: recoveryPasswordSelectors.getFormFieldErrors(state) ?? undefined,
});

export default compose(connect(mapStateToProps), withExchangeToken)(RecoveryUserpassStep3V3);
